import type { PostalCodeOption } from '@houzy/schemas/partner-cockpit/company';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { lazy, Suspense, useCallback, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { setsEqual } from '../../../lib/sets';
import { useDebounce } from '../../../lib/useDebounce';
import {
    useCompanyLocation,
    usePostalCodesByCanton,
    usePostalCodesInRange,
    useSaveWorkRegionSelection,
} from '../api/workRegionsApi';
import { cantons } from '../data/cantons';
import { useDirtyForm } from '../state/useDirtyForm';
import { useWorkRegionTree } from '../state/useWorkRegionTree';
import type { WorkRegion } from '../types';
import { SettingsCard } from './SettingsCard';
import { WorkRegionTree } from './WorkRegionTree';

const RegionMap = lazy(() => import('./RegionMap').then((m) => ({ default: m.RegionMap })));

export type WorkRegionCardProps = {
    region: WorkRegion;
    readOnly?: boolean;
};

// A fresh `{}` fallback each render would re-run the tree memo, whose effect resets the expanded cantons —
// an update loop that only the route loader's prefetch currently hides.
const NO_POSTAL_CODES_BY_CANTON: Record<string, PostalCodeOption[]> = {};

const DISTANCE_MIN_KM = 0;
const DISTANCE_MAX_KM = 50;
const DISTANCE_STEP_KM = 5;

function regionsEqual(a: WorkRegion, b: WorkRegion): boolean {
    return setsEqual(a.postalCodes, b.postalCodes);
}

export function WorkRegionCard({ region, readOnly = false }: WorkRegionCardProps): ReactElement {
    const { t } = useTranslation();
    const { value, setValue, reset, isDirty } = useDirtyForm<WorkRegion>(region, regionsEqual);
    // Place and distance filter the tree and the map; neither is persisted, so they stay out of the dirty form.
    const [place, setPlace] = useState('');
    const [distanceKm, setDistanceKm] = useState(DISTANCE_MIN_KM);
    const postalCodesByCantonQuery = usePostalCodesByCanton();
    const companyLocationQuery = useCompanyLocation();
    const debouncedDistanceKm = useDebounce(distanceKm, 300);
    const rangeQuery = usePostalCodesInRange(debouncedDistanceKm);
    const saveMutation = useSaveWorkRegionSelection();

    const companyLocation = companyLocationQuery.data ?? undefined;
    // The range is measured by driving distance from the company address, so without a geocoded
    // address the backend has no origin to measure from.
    const hasNoCompanyLocation = companyLocationQuery.data === null;
    const distanceDisabled = place !== '' || companyLocation === undefined;

    const distanceMarks = useMemo(
        () => [
            { value: DISTANCE_MIN_KM, label: t('SETTINGS.COMPANY.WORK_REGION.DISTANCE_MARK', { km: DISTANCE_MIN_KM }) },
            { value: DISTANCE_MAX_KM, label: t('SETTINGS.COMPANY.WORK_REGION.DISTANCE_MARK', { km: DISTANCE_MAX_KM }) },
        ],
        [t],
    );

    const handlePlaceChange = (next: string): void => {
        setPlace(next);
        // The two filters would otherwise intersect, which reads as the slider having no effect.
        if (next !== '') {
            setDistanceKm(DISTANCE_MIN_KM);
        }
    };

    const handlePostalCodesChange = useCallback(
        (next: ReadonlySet<string>) => {
            setValue((prev) => ({ ...prev, postalCodes: next }));
        },
        [setValue],
    );

    const rangeFilterCodes = useMemo(
        () => (distanceKm > DISTANCE_MIN_KM && rangeQuery.data ? new Set(rangeQuery.data) : undefined),
        [distanceKm, rangeQuery.data],
    );

    const tree = useWorkRegionTree({
        cantons,
        postalCodesByCanton: postalCodesByCantonQuery.data ?? NO_POSTAL_CODES_BY_CANTON,
        selectedPostalCodes: value.postalCodes,
        onSelectionChange: handlePostalCodesChange,
        search: place,
        rangeFilterCodes,
    });

    const handleSave = (): void => {
        saveMutation.mutate(value.postalCodes);
    };

    const handleReset = (): void => {
        reset();
        setPlace('');
        setDistanceKm(DISTANCE_MIN_KM);
    };

    const distanceLabel =
        distanceKm === DISTANCE_MIN_KM
            ? t('SETTINGS.COMPANY.WORK_REGION.DISTANCE_UNSET')
            : t('SETTINGS.COMPANY.WORK_REGION.DISTANCE', { km: distanceKm });

    return (
        <SettingsCard
            title={t('SETTINGS.COMPANY.WORK_REGION.TITLE')}
            dirty={isDirty}
            saving={saveMutation.isPending}
            onSave={handleSave}
            onReset={handleReset}
            readOnly={readOnly}>
            {hasNoCompanyLocation && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('SETTINGS.COMPANY.WORK_REGION.NO_ADDRESS_ALERT')}
                </Alert>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '265px minmax(0, 1fr)' },
                    gap: 2,
                    alignItems: 'start',
                }}>
                <Stack spacing={3} sx={{ order: { xs: 2, md: 0 } }}>
                    <Stack spacing={1}>
                        <Typography variant="subtitle2" component="label" htmlFor="settings-region-place">
                            {t('SETTINGS.COMPANY.WORK_REGION.PLACES')}
                        </Typography>
                        <TextField
                            id="settings-region-place"
                            size="small"
                            placeholder={t('SETTINGS.COMPANY.WORK_REGION.PLACES_PLACEHOLDER')}
                            value={place}
                            onChange={(e) => handlePlaceChange(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Stack>

                    <Stack spacing={1}>
                        <Typography
                            variant="subtitle2"
                            component="span"
                            sx={{ color: distanceDisabled ? 'text.disabled' : undefined }}>
                            {distanceLabel}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                            <PlaceOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: '1px' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('SETTINGS.COMPANY.WORK_REGION.DISTANCE_FROM_HEADQUARTERS')}
                            </Typography>
                        </Stack>
                        <Tooltip
                            title={place !== '' ? t('SETTINGS.COMPANY.WORK_REGION.DISTANCE_DISABLED_BY_PLACE') : ''}>
                            <Box sx={{ px: 1 }}>
                                <Slider
                                    value={distanceKm}
                                    onChange={(_, next) =>
                                        setDistanceKm(Array.isArray(next) ? (next[0] ?? DISTANCE_MIN_KM) : next)
                                    }
                                    disabled={distanceDisabled}
                                    min={DISTANCE_MIN_KM}
                                    max={DISTANCE_MAX_KM}
                                    step={DISTANCE_STEP_KM}
                                    marks={distanceMarks}
                                    valueLabelDisplay="auto"
                                    aria-label={t('SETTINGS.COMPANY.WORK_REGION.SEARCH_RADIUS_ARIA')}
                                />
                            </Box>
                        </Tooltip>
                    </Stack>

                    {postalCodesByCantonQuery.isLoading ? (
                        <Stack spacing={0.5} aria-busy>
                            {Array.from({ length: 10 }).map((_, i) => (
                                <Skeleton key={i} variant="rounded" height={40} />
                            ))}
                        </Stack>
                    ) : (
                        <WorkRegionTree
                            filteredCantonNodes={tree.filteredCantonNodes}
                            expandedCantons={tree.expandedCantons}
                            selectedPostalCodes={value.postalCodes}
                            toggleExpandCanton={tree.toggleExpandCanton}
                            toggleCantonSelection={tree.toggleCantonSelection}
                            togglePostalCode={tree.togglePostalCode}
                            disabled={readOnly}
                        />
                    )}
                </Stack>

                <Box sx={{ order: { xs: 1, md: 0 } }}>
                    <Suspense
                        fallback={
                            <Skeleton
                                variant="rounded"
                                sx={{ width: '100%', minHeight: { xs: 320, md: 480 } }}
                                aria-label={t('SETTINGS.COMPANY.WORK_REGION.LOADING_MAP')}
                            />
                        }>
                        <RegionMap
                            selectedPostalCodes={value.postalCodes}
                            visiblePostalCodes={tree.visiblePostalCodes}
                            onTogglePostalCode={readOnly ? undefined : tree.togglePostalCode}
                            companyLocation={companyLocation}
                            companyLocationMissing={hasNoCompanyLocation}
                        />
                    </Suspense>
                </Box>
            </Box>
        </SettingsCard>
    );
}
